import React, { useState, useEffect, useRef } from 'react';
import { Popover, Box, Typography, Checkbox, CircularProgress, PopoverProps } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Folder, setGameFolders, getGameFolderIds } from '../../services/folders_api';
import { folderCache } from '../../services/folders_cache';
import { FolderGlyph, folderColor } from './folderVisuals';
import { useUser } from '../../contexts/UserContext';
import { getFolderItemMaxCount, FOLDER_ITEM_LIMITS } from '../../utils/subscription';
import PlanUpgradeCTA from '../common/PlanUpgradeCTA';

interface FolderPickerPopoverProps {
  open: boolean;
  // 右クリック時はカーソル位置の仮想アンカー要素を受け取るため、MUIのアンカー型に合わせる
  anchorEl: PopoverProps['anchorEl'];
  onClose: () => void;
  folders: Folder[];
  gameId: number;
  // 保存完了時に親へ通知（キャッシュ無効化等）。ids は渡さない。
  onChange: () => void;
}

const sameSet = (a: number[], b: number[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

// 1ゲームの所属フォルダをチェックリストで編集するポップオーバー。
// open 時に game_folders エンドポイントで初期状態を遅延取得する。
const FolderPickerPopover: React.FC<FolderPickerPopoverProps> = ({
  open, anchorEl, onClose, folders, gameId, onChange,
}) => {
  const [picked, setPicked] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const initialRef = useRef<number[]>([]);
  const queryClient = useQueryClient();
  const { user } = useUser();
  const plan = user?.subscription_plan ?? 0;
  const itemLimit = getFolderItemMaxCount(plan);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getGameFolderIds(gameId)
      .then((ids) => {
        setPicked(ids);
        initialRef.current = ids;
      })
      .catch(() => {
        setPicked([]);
        initialRef.current = [];
      })
      .finally(() => setLoading(false));
  }, [open, gameId]);

  const toggle = (id: number) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleClose = async () => {
    onClose();
    if (sameSet(picked, initialRef.current)) return;
    try {
      const res = await setGameFolders(gameId, picked);
      // 変更が起きたフォルダの確定件数でキャッシュを直接更新（refetch なし）。
      folderCache.setCounts(queryClient, res.item_counts);
      onChange();
    } catch (e) {
      console.error('フォルダの更新に失敗しました', e);
      folderCache.invalidate(queryClient); // 失敗時は真値を取り直す
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          minWidth: 220,
          maxWidth: 280,
          mt: 0.5,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
        },
      }}
    >
      <Box sx={{ p: 0.75 }}>
        <Typography sx={{ px: 1, py: 0.5, fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary' }}>
          フォルダに入れる
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : folders.length === 0 ? (
          <Typography sx={{ px: 1, py: 1.5, fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
            フォルダがありません。フォルダバーの ＋ から作成できます。
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {folders.map((folder) => {
              const checked = picked.includes(folder.id);
              const color = folderColor(folder.color);
              // 満杯かつ未所属のフォルダは追加不可（解除はできるので所属中は除外）。
              const blocked = !checked && folder.item_count >= itemLimit;
              return (
                <Box
                  key={folder.id}
                  onClick={() => { if (!blocked) toggle(folder.id); }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.5,
                    py: 0.4,
                    borderRadius: '8px',
                    cursor: blocked ? 'not-allowed' : 'pointer',
                    opacity: blocked ? 0.5 : 1,
                    '&:hover': { backgroundColor: blocked ? 'transparent' : 'action.hover' },
                  }}
                >
                  <Checkbox
                    checked={checked}
                    disabled={blocked}
                    size="small"
                    disableRipple
                    sx={{ p: 0.5, color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color } }}
                  />
                  <FolderGlyph iconKey={folder.icon} open={checked} sx={{ fontSize: '1rem', color }} />
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: '0.8rem',
                      fontWeight: checked ? 600 : 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {folder.name}
                  </Typography>
                  {blocked ? (
                    <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.4)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      上限
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>
                      {folder.item_count}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* 満杯フォルダがあればプラン誘導 */}
        {!loading && folders.some((f) => !picked.includes(f.id) && f.item_count >= itemLimit) && (
          <Box sx={{ px: 0.5, pt: 0.5 }}>
            <PlanUpgradeCTA
              currentPlan={plan}
              currentValue={itemLimit}
              limits={FOLDER_ITEM_LIMITS}
              unit="件"
              verb="入れられ"
              showMultiplier={false}
            />
          </Box>
        )}
      </Box>
    </Popover>
  );
};

export default FolderPickerPopover;
